import { ArrowForward, CheckCircle } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  Grid,
  Link,
  Stack,
  SvgIcon,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatPhoneNumber } from 'react-phone-number-input';
import ProfileFirstName from './ProfileComponents/ProfileFirstName';
import ProfileLastName from './ProfileComponents/ProfileLastName';
import ProfilePhone from './ProfileComponents/ProfilePhone';
import ProfileTitle from './ProfileComponents/ProfileTitle';
import ProfileImage from './ProfileImage';
import { Logo } from '.';

const Header = ({ isVendor, logo }) => {
  const theme = useTheme();
  return (
    <Box
      data-testid="signInScreenHeader"
      sx={{
        width: '100%',
        minHeight: '97px',
        backgroundColor: isVendor
          ? theme.palette.background.paper
          : theme.palette.common.black,
        boxShadow: theme.shadows[3],
        paddingLeft: '40px',
        paddingBottom: '6px',
      }}
    >
      <Logo
        width="100%"
        height="100%"
        maxWidth="202px"
        isVendor={isVendor}
        logo={logo}
      />
    </Box>
  );
};

const FormHeader = ({ amplifyApi, data, setValue, isSignIn }) => {
  const { t } = useTranslation('global');
  return (
    <>
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Typography variant="h4">{t('signIn.formHeader.title')}</Typography>
          <Typography variant="body1" color="GrayText">
            {t('signIn.formHeader.subTitle')}
          </Typography>
        </Box>
        <ProfileImage
          amplifyApi={amplifyApi}
          isSignIn={isSignIn}
          url={data?.photo}
          onChange={photo => {
            setValue('photo', photo?.fileId || null, { shouldDirty: true });
          }}
        />
      </Stack>
      <Box sx={{ paddingTop: '30px', paddingBottom: '40px' }}>
        <Divider />
      </Box>
    </>
  );
};

const InitForm = ({
  amplifyApi,
  data,
  setValue,
  errors,
  register,
  control,
  isLoading,
  isValid,
  isDirty,
  handleSubmit,
  onSave,
  isSignIn,
}) => {
  const { t } = useTranslation('global');
  const theme = useTheme();

  return (
    <Stack
      data-testid="sigInInitialForm"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      <Box
        sx={{
          maxWidth: '700px',
          width: '100%',
          padding: 3,
          border: '1px solid',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
        }}
      >
        <FormHeader
          amplifyApi={amplifyApi}
          data={data}
          setValue={setValue}
          isSignIn={isSignIn}
        />
        <Stack flexDirection="column" justifyContent="flex-end" gap={2}>
          <Grid container flexDirection="row" spacing={4}>
            <Grid item xs={12} md={6}>
              <ProfileFirstName
                errors={errors}
                data={data}
                register={register}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProfileLastName
                errors={errors}
                data={data}
                register={register}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProfileTitle errors={errors} data={data} register={register} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProfilePhone control={control} data={data} errors={errors} />
            </Grid>
          </Grid>
          <Stack flexDirection="row" justifyContent="flex-end">
            <LoadingButton
              data-testid="next-button"
              variant="contained"
              color="primary"
              size="large"
              loading={isLoading}
              disabled={!isValid || !isDirty}
              onClick={handleSubmit(onSave)}
            >
              {t('signIn.form.button')}
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

const VerificationForm = ({
  phone,
  verificationCode,
  error,
  setVerificationCode,
  verificationCodeLoading,
  onGoBack,
  onResend,
  onVerify,
}) => {
  const { t } = useTranslation('global');
  const theme = useTheme();
  const extraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Stack
      data-testid="sigInInitialForm"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      <Box
        sx={{
          maxWidth: '700px',
          width: '100%',
          padding: 3,
          border: '1px solid',
          height: '400px',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
        }}
      >
        <Stack
          direction="column"
          justifyContent="space-between"
          sx={{ height: '100%' }}
        >
          <Stack>
            <Stack direction="column" gap={1} justifyContent="center">
              <Typography variant={extraSmall ? 'h5' : 'h4'}>
                {t('signIn.phoneVerify.title', {
                  extra: extraSmall ? '' : t('signIn.phoneVerify.titleExtra'),
                })}
              </Typography>
              <Typography variant="body2" color="GrayText" sx={{ mr: 1 }}>
                {t('phoneVerification.subTitle')}
                <b> {formatPhoneNumber(phone)}. </b>
                <Link
                  component="button"
                  variant="body2"
                  sx={{
                    paddingBottom: '2px',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underLine' },
                  }}
                  onClick={onGoBack}
                >
                  {t('phoneVerification.edit')}
                </Link>
              </Typography>
            </Stack>
            <Box sx={{ paddingTop: '20px', paddingBottom: '35px' }}>
              <Divider />
            </Box>
            <Stack direction="column" gap={2}>
              <TextField
                type="text"
                label={t('phoneVerification.verification')}
                required={true}
                size="small"
                data-testid="codeInput"
                fullWidth
                sx={{ maxWidth: '300px' }}
                value={verificationCode}
                error={!!error}
                helperText={error}
                onChange={event => setVerificationCode(event.target.value)}
              />

              <Typography variant="body2" color="GrayText" sx={{ mr: 1 }}>
                {t('phoneVerification.dontReceived')}
                <Link
                  component="button"
                  variant="body2"
                  sx={{
                    paddingBottom: '2px',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underLine' },
                    ml: '4px',
                  }}
                  onClick={onResend}
                >
                  {t('phoneVerification.resend')}
                </Link>
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Button variant="outlined" size="large" onClick={onGoBack}>
              {t('signIn.skip')}
            </Button>
            <LoadingButton
              loading={verificationCodeLoading}
              variant="contained"
              size="large"
              onClick={() => onVerify(verificationCode)}
            >
              {t('phoneVerification.verify')}
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

const SuccessScreen = ({ successScreenOnClick }) => {
  const { t } = useTranslation('global');
  const theme = useTheme();
  return (
    <Stack
      data-testid="sigInInSuccess"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      <Box
        sx={{
          maxWidth: '400px',
          width: '100%',
          px: 3,
          py: 4,
          border: '1px solid',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
        }}
      >
        <Stack
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap={1}
        >
          <SvgIcon
            color="success"
            sx={{ fontSize: '71px', mt: 2, mb: 1 }}
            component={CheckCircle}
          ></SvgIcon>
          <Typography variant="h6">
            {t('signIn.successScreen.title')}
          </Typography>
          <Typography variant="body1" color="GrayText">
            {t('signIn.successScreen.subTitle')}
          </Typography>
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            size="large"
            sx={{ mt: 4, mb: 6 }}
            onClick={successScreenOnClick}
          >
            {t('signIn.successScreen.button')}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

const SignIn = ({
  isVendor = true,
  isLoading,
  amplifyApi,
  data,
  onSave,
  logo,
  profileStatus,
  isSignIn = true,
  successScreenOnClick = () => {},
  error,
  verificationCodeOpen,
  verificationCodeLoading,
  onGoBack,
  onResend,
  onVerify,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { isValid, errors, isDirty },
  } = useForm({
    mode: 'all',
  });

  useEffect(() => {
    if (!data) return;
    reset({
      firstName: data.firstName,
      lastName: data.lastName,
      title: data.title,
      phone: data.phone,
      photo: data.photo,
    });
  }, [reset, data]);

  const [verificationCode, setVerificationCode] = useState('');

  return (
    <Stack
      sx={{
        width: '100%',
        height: '100vh',
        backgroundColor: '#f8f8f8',
      }}
      flexDirection="column"
      alignItems="center"
      data-testid="signInComponent"
    >
      <Header isVendor={isVendor} logo={logo} />
      {profileStatus === 'success' && !verificationCodeOpen && (
        <SuccessScreen successScreenOnClick={successScreenOnClick} />
      )}
      {profileStatus === 'success' && verificationCodeOpen && (
        <VerificationForm
          phone={data?.phone}
          verificationCode={verificationCode}
          error={error}
          setVerificationCode={setVerificationCode}
          verificationCodeLoading={verificationCodeLoading}
          onGoBack={onGoBack}
          onResend={onResend}
          onVerify={onVerify}
        />
      )}
      {profileStatus !== 'success' && !verificationCodeOpen && (
        <InitForm
          amplifyApi={amplifyApi}
          data={data}
          setValue={setValue}
          errors={errors}
          register={register}
          control={control}
          isLoading={isLoading}
          isValid={isValid}
          isDirty={isDirty}
          handleSubmit={handleSubmit}
          onSave={onSave}
          isSignIn={isSignIn}
        />
      )}
    </Stack>
  );
};

export default SignIn;
